import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFViewerProps } from '../types';
import { 
  loadPDFDocument, 
  renderPDFPage, 
  generatePDFUrl,
  validateRecordId 
} from '../utils/pdfUtils';
import styles from './PDFViewer.module.css';

interface ViewerState {
  scale: number;
  rotation: number;
  fitMode: 'width' | 'height' | 'page' | 'custom';
}

const PDFViewer: React.FC<PDFViewerProps> = ({ 
  recordId, 
  url, 
  onLoad, 
  onError 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfDocument, setPdfDocument] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [viewerState, setViewerState] = useState<ViewerState>({
    scale: 1.0,
    rotation: 0,
    fitMode: 'width'
  });
  const [pageInfo, setPageInfo] = useState<{ width: number; height: number } | null>(null);
  const [isRendering, setIsRendering] = useState<boolean>(false);

  const pdfUrl = url || generatePDFUrl(recordId);

  const loadPDF = useCallback(async () => {
    if (!validateRecordId(recordId)) {
      const error = new Error('Invalid record ID format');
      setError(error.message);
      onError?.(error);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const doc = await loadPDFDocument(pdfUrl);
      setPdfDocument(doc);
      setTotalPages(doc.numPages);
      setCurrentPage(1);
      
      onLoad?.();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load PDF');
      setError(error.message);
      onError?.(error);
    } finally {
      setLoading(false);
    }
  }, [recordId, pdfUrl, onLoad, onError]);

  const calculateOptimalScale = useCallback((pageWidth: number, pageHeight: number) => {
    if (!containerRef.current) return 1.0;
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth - 40; // Account for padding
    const containerHeight = container.clientHeight - 40;
    
    const widthScale = containerWidth / pageWidth;
    const heightScale = containerHeight / pageHeight;
    
    switch (viewerState.fitMode) {
      case 'width':
        return Math.min(widthScale, 2.0); // Max 200% zoom
      case 'height':
        return Math.min(heightScale, 2.0);
      case 'page':
        return Math.min(Math.min(widthScale, heightScale), 2.0);
      case 'custom':
        return viewerState.scale;
      default:
        return 1.0;
    }
  }, [viewerState.fitMode, viewerState.scale]);

  const renderPage = useCallback(async () => {
    if (!pdfDocument || !canvasRef.current) return;

    try {
      setIsRendering(true);
      const page = await pdfDocument.getPage(currentPage);
      
      // Get page dimensions
      const viewport = page.getViewport({ scale: 1.0, rotation: 0 });
      setPageInfo({ width: viewport.width, height: viewport.height });
      
      // Calculate optimal scale
      const optimalScale = calculateOptimalScale(viewport.width, viewport.height);
      const finalScale = viewerState.fitMode === 'custom' ? viewerState.scale : optimalScale;
      
      await renderPDFPage(page, canvasRef.current, finalScale, viewerState.rotation);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to render page');
      setError(error.message);
      onError?.(error);
    } finally {
      setIsRendering(false);
    }
  }, [pdfDocument, currentPage, viewerState, calculateOptimalScale, onError]);

  const goToPage = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleZoomIn = () => {
    setViewerState(prev => ({
      ...prev,
      scale: Math.min(prev.scale * 1.2, 3.0),
      fitMode: 'custom'
    }));
  };

  const handleZoomOut = () => {
    setViewerState(prev => ({
      ...prev,
      scale: Math.max(prev.scale / 1.2, 0.5),
      fitMode: 'custom'
    }));
  };

  const handleRotate = () => {
    setViewerState(prev => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360
    }));
  };

  const handleFitMode = (mode: 'width' | 'height' | 'page') => {
    setViewerState(prev => ({
      ...prev,
      fitMode: mode
    }));
  };

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowLeft':
        if (currentPage > 1) goToPage(currentPage - 1);
        break;
      case 'ArrowRight':
        if (currentPage < totalPages) goToPage(currentPage + 1);
        break;
      case 'Home':
        goToPage(1);
        break;
      case 'End':
        goToPage(totalPages);
        break;
      case '+':
      case '=':
        event.preventDefault();
        handleZoomIn();
        break;
      case '-':
        event.preventDefault();
        handleZoomOut();
        break;
      case 'r':
        event.preventDefault();
        handleRotate();
        break;
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    loadPDF();
  }, [loadPDF]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    console.log('PDFViewer rendering with enhanced controls');
  }, []);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading PDF...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <h3 className={styles.errorTitle}>Unable to Load PDF</h3>
        <p className={styles.errorMessage}>
          {error.includes('CORS') ? 'Access denied. Please check file permissions.' :
           error.includes('404') ? 'PDF not found. Please verify the URL.' :
           error.includes('NetworkError') ? 'Network error. Please check your connection.' :
           'The PDF could not be loaded. Please try again.'}
        </p>
        <div className={styles.errorActions}>
          <button 
            onClick={loadPDF}
            className={styles.retryButton}
          >
            Retry
          </button>
          <button 
            onClick={() => window.open(pdfUrl, '_blank')}
            className={styles.openButton}
          >
            Open in New Tab
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Enhanced controls */}
      <div className={styles.toolbar}>
        <div className={styles.navigation}>
          <button 
            onClick={() => goToPage(1)}
            disabled={currentPage <= 1}
            className={styles.toolbarButton}
            title="First page (Home)"
          >
            ⏮
          </button>
          <button 
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            className={styles.toolbarButton}
            title="Previous page (←)"
          >
            ◀
          </button>
          
          <div className={styles.pageInput}>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (!isNaN(page)) goToPage(page);
              }}
              className={styles.pageInputField}
            />
            <span className={styles.pageInfo}>of {totalPages}</span>
          </div>
          
          <button 
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className={styles.toolbarButton}
            title="Next page (→)"
          >
            ▶
          </button>
          <button 
            onClick={() => goToPage(totalPages)}
            disabled={currentPage >= totalPages}
            className={styles.toolbarButton}
            title="Last page (End)"
          >
            ⏭
          </button>
        </div>

        <div className={styles.zoomControls}>
          <button 
            onClick={handleZoomOut}
            className={styles.toolbarButton}
            title="Zoom out (-)"
          >
            🔍-
          </button>
          <span className={styles.zoomLevel}>
            {Math.round(viewerState.scale * 100)}%
          </span>
          <button 
            onClick={handleZoomIn}
            className={styles.toolbarButton}
            title="Zoom in (+)"
          >
            🔍+
          </button>
        </div>

        <div className={styles.fitControls}>
          <button 
            onClick={() => handleFitMode('width')}
            className={`${styles.toolbarButton} ${viewerState.fitMode === 'width' ? styles.active : ''}`}
            title="Fit to width"
          >
            ↔
          </button>
          <button 
            onClick={() => handleFitMode('height')}
            className={`${styles.toolbarButton} ${viewerState.fitMode === 'height' ? styles.active : ''}`}
            title="Fit to height"
          >
            ↕
          </button>
          <button 
            onClick={() => handleFitMode('page')}
            className={`${styles.toolbarButton} ${viewerState.fitMode === 'page' ? styles.active : ''}`}
            title="Fit to page"
          >
            ⤢
          </button>
        </div>

        <button 
          onClick={handleRotate}
          className={styles.toolbarButton}
          title="Rotate (R)"
        >
          🔄
        </button>
      </div>

      {/* PDF canvas container */}
      <div className={styles.canvasContainer} ref={containerRef}>
        {isRendering && (
          <div className={styles.renderingOverlay}>
            <div className={styles.spinner}></div>
            <p>Rendering...</p>
          </div>
        )}
        <canvas ref={canvasRef} className={styles.canvas} />
      </div>

      {/* Page info */}
      {pageInfo && (
        <div className={styles.pageDetails}>
          <span>Page {currentPage} of {totalPages}</span>
          <span>•</span>
          <span>{Math.round(pageInfo.width)} × {Math.round(pageInfo.height)} px</span>
          <span>•</span>
          <span>{Math.round(viewerState.scale * 100)}% zoom</span>
          {viewerState.rotation !== 0 && (
            <>
              <span>•</span>
              <span>{viewerState.rotation}° rotation</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PDFViewer; 